import { ExceptionManager } from '../../helpers/classes/ExceptionManager';
import { FlowState } from '../enums/FlowState';
import { RequiredAction } from '../enums/RequiredAction';
import { TranslationKey } from '../enums/Translation';

export class JourneyFlow {
	public get state(): FlowState {
		return this._state;
	}

	public get attempt(): number {
		return this._attempt;
	}

	public get history(): FlowState[] {
		return this._history;
	}

	private _state: FlowState;
	private _history: FlowState[] = [];
	private _attempt: number;

	constructor(
		state: FlowState = FlowState.IDENTITY_FRONT,
		attempt: number = 1
	) {
		this._state = state;
		this._attempt = attempt;
		this.setFlowVariables(this._state, this._attempt);
	}

	/**
	 * Takes an internal state identifier and parses it into state and attempt
	 * @param internalState
	 */
	public proceed = (internalState: RequiredAction): Promise<void> => {
		switch (internalState) {
			case RequiredAction.FRONTSIDE_FIRST_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_FRONT, 1);
			case RequiredAction.FRONTSIDE_SECOND_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_FRONT, 2);
			case RequiredAction.FRONTSIDE_THIRD_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_FRONT, 3);
			case RequiredAction.BACKSIDE_FIRST_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_BACK, 1);
			case RequiredAction.BACKSIDE_SECOND_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_BACK, 2);
			case RequiredAction.BACKSIDE_THIRD_SCAN:
				return this.setFlowVariables(FlowState.IDENTITY_BACK, 3);
			case RequiredAction.ADDRESSDOCUMENT_FIRST_SCAN:
				return this.setFlowVariables(FlowState.ADDRESS, 1);
			case RequiredAction.ADDRESSDOCUMENT_SECOND_SCAN:
				return this.setFlowVariables(FlowState.ADDRESS, 2);
			case RequiredAction.ADDRESSDOCUMENT_THIRD_SCAN:
				return this.setFlowVariables(FlowState.ADDRESS, 3);
			case RequiredAction.SELFIE_FIRST_SCAN:
				return this.setFlowVariables(FlowState.SELFIE, 1);
			case RequiredAction.SELFIE_SECOND_SCAN:
				return this.setFlowVariables(FlowState.SELFIE, 2);
			case RequiredAction.SELFIE_THIRD_SCAN:
				return this.setFlowVariables(FlowState.SELFIE, 3);
			case RequiredAction.LIVENESS:
				return this.setFlowVariables(FlowState.LIVENESS, 1);
			case RequiredAction.PASSIVE_LIVENESS:
				return this.setFlowVariables(FlowState.PASSIVE_LIVENESS, 1);
			case RequiredAction.COVIDPASS_FIRST_SCAN:
				return this.setFlowVariables(FlowState.COVIDPASS, 1);
			case RequiredAction.COVIDPASS_SECOND_SCAN:
				return this.setFlowVariables(FlowState.COVIDPASS, 2);
			case RequiredAction.COVIDPASS_THIRD_SCAN:
				return this.setFlowVariables(FlowState.COVIDPASS, 3);
			case RequiredAction.DATACHECKS:
				return this.setFlowVariables(FlowState.DATACHECKS, 1);
			case RequiredAction.NONE:
				return this.setFlowVariables(FlowState.NONE, 0);
			default:
				return Promise.reject(
					ExceptionManager.process(
						new ExceptionManager.FlowStateException(
							TranslationKey.EXCEPTION_FLOW_STATE_MISSING,
							internalState
						)
					)
				);
		}
	};

	/**
	 * Sets the state and attempt based on the interpreter results
	 * @param state
	 * @param attempt
	 */
	private setFlowVariables = (
		state: FlowState,
		attempt: number
	): Promise<void> => {
		this._attempt = attempt;
		this._state = state;

		return this.pushState();
	};

	/**
	 * Pushes the current state into the history stack.
	 */
	private pushState = (): Promise<void> => {
		this._history.push(this._state);

		return Promise.resolve();
	};
}
